import { Skeleton, Space, Typography } from 'antd';
import { FC, ReactNode, useEffect } from 'react';
import {
  Controller,
  ControllerProps,
  get,
  useFormContext,
} from 'react-hook-form';

const { Text } = Typography;

interface FormItemProps {
  label?: ReactNode;
  labelType?: 'default' | 'secondary';
  required?: boolean;
  isLoading?: boolean;
  loadingType?: 'field' | 'options';
  unregisterOnUnmount?: boolean;
  control: ControllerProps<any>['control'];
  name: ControllerProps['name'];
  defaultValue?: ControllerProps['defaultValue'];
  renderItem: ControllerProps['render'];
}

export const FormItem: FC<FormItemProps> = ({
  label,
  labelType = 'default',
  required,
  isLoading,
  loadingType = 'field',
  unregisterOnUnmount,
  control,
  name,
  defaultValue,
  renderItem,
}) => {
  const {
    unregister,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    return () => {
      if (unregisterOnUnmount) {
        unregister(name);
      }
    };
  }, [unregisterOnUnmount, name]);

  const loading = {
    field: <Skeleton.Input active block />,
    options: <OptionsSkeleton />,
  };

  const error = get(errors, name);

  return (
    <Space direction='vertical' size={4} style={{ width: '100%' }}>
      {!!label && (
        <Text type={labelType === 'secondary' ? 'secondary' : undefined}>
          {label}
          {required && (
            <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
          )}
        </Text>
      )}
      {isLoading ? (
        loading[loadingType]
      ) : (
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue}
          render={renderItem}
        />
      )}
      {!!error && <Text type='danger'>{error.message}</Text>}
    </Space>
  );
};

const OptionsSkeleton: FC = () => {
  return (
    <Space style={{ width: '100%' }}>
      {new Array(6).fill(0).map((_, index) => (
        <Skeleton.Button active key={index} style={{ width: '100px' }} />
      ))}
    </Space>
  );
};
